import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { COMMUNITY_IMPORT_MAX_STATUS_POLL_FAILURES, COMMUNITY_IMPORT_POLL_INTERVAL_MS } from '@/constants';
import {
  getCommunityImportCurrentQueryKey,
  getCommunityImportStatusQueryKey,
  invalidateCommunityDataForChurch,
  isCommunityImportNotFoundError,
} from '@/helpers';
import communityService from '@/services/community.service';
import { COMMUNITY_IMPORT_STATUS, type CommunityImportStatusResponse } from '@/types';

type UseCommunityListImportStatusOptions = {
  churchId: string;
  listId?: string;
  enabled?: boolean;
};

type HandleImportStartedParams = {
  processId: string;
};

const TERMINAL_STATUSES: COMMUNITY_IMPORT_STATUS[] = [COMMUNITY_IMPORT_STATUS.DONE, COMMUNITY_IMPORT_STATUS.ERROR];

const isTerminalStatus = (status?: COMMUNITY_IMPORT_STATUS) => Boolean(status && TERMINAL_STATUSES.includes(status));

export const useCommunityListImportStatus = ({
  churchId,
  listId,
  enabled = true,
}: UseCommunityListImportStatusOptions) => {
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();
  const [trackedProcessId, setTrackedProcessId] = useState<string | null>(null);
  const [hasPollError, setHasPollError] = useState<boolean>(false);
  const reportedProcessIdRef = useRef<string | null>(null);
  const scopeRef = useRef<string | null>(null);
  const statusPollFailureCountRef = useRef<number>(0);
  const lastStatusDataUpdatedAtRef = useRef<number>(0);

  const isEnabled = enabled && Boolean(churchId) && Boolean(listId);
  const scopeKey = `${churchId}:${listId || ''}`;

  // Switching lists must never carry a tracked process over to another list.
  useEffect(() => {
    if (scopeRef.current === scopeKey) {
      return;
    }

    scopeRef.current = scopeKey;
    reportedProcessIdRef.current = null;
    statusPollFailureCountRef.current = 0;
    lastStatusDataUpdatedAtRef.current = 0;
    setHasPollError(false);
    setTrackedProcessId(null);
  }, [scopeKey]);

  const currentImportQuery = useQuery({
    queryKey: getCommunityImportCurrentQueryKey({ churchId, listId: listId || '' }),
    queryFn: ({ signal }) => communityService.getCurrentListImport({ churchId, listId: listId || '', signal }),
    enabled: isEnabled,
    staleTime: Infinity,
    retry: false,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    meta: {
      suppressGlobalError: true,
    },
  });

  const { refetch: refetchCurrentImportQuery } = currentImportQuery;

  const refetchCurrentImport = useCallback(async () => {
    const { data } = await refetchCurrentImportQuery();

    if (data?.processId) {
      reportedProcessIdRef.current = null;
      setTrackedProcessId(data.processId);
      return;
    }

    setTrackedProcessId(null);
  }, [refetchCurrentImportQuery]);

  const statusQuery = useQuery({
    queryKey: getCommunityImportStatusQueryKey({
      churchId,
      listId: listId || '',
      processId: trackedProcessId || '',
    }),
    queryFn: ({ signal }) =>
      communityService.getListImportStatus({
        churchId,
        listId: listId || '',
        processId: trackedProcessId || '',
        signal,
      }),
    enabled: isEnabled && Boolean(trackedProcessId),
    retry: false,
    refetchOnWindowFocus: false,
    meta: {
      suppressGlobalError: true,
    },
    refetchInterval: (query) => {
      if (hasPollError || isTerminalStatus(query.state.data?.status)) {
        return false;
      }

      // A failed poll leaves data unchanged, so polling continues instead of being mistaken for completion.
      return COMMUNITY_IMPORT_POLL_INTERVAL_MS;
    },
  });

  const currentProcessId = currentImportQuery.data?.processId;

  useEffect(() => {
    if (!currentProcessId || reportedProcessIdRef.current === currentProcessId) {
      return;
    }

    setTrackedProcessId(currentProcessId);
  }, [currentProcessId]);

  useEffect(() => {
    statusPollFailureCountRef.current = 0;
    setHasPollError(false);
  }, [trackedProcessId]);

  const notifyImportFinished = useCallback(
    ({ result }: { result: CommunityImportStatusResponse }) => {
      if (result.status === COMMUNITY_IMPORT_STATUS.ERROR) {
        showNotification({
          id: `community-import-failed-${result.processId}`,
          title: t('community.cards.importFailedTitle'),
          message: t('community.cards.importFailedMessage'),
          color: 'fail',
          autoClose: false,
        });
        return;
      }

      showNotification({
        id: `community-import-completed-${result.processId}`,
        title: t('community.cards.importCompletedTitle'),
        message: t('community.cards.importCompletedMessage', {
          createdCards: result.createdCards,
          createdPeople: result.createdPeople,
          skippedPeople: result.skippedPeople,
          failedCards: result.failedCards,
        }),
        color: 'brand',
        autoClose: false,
      });
    },
    [t]
  );

  const statusResult = statusQuery.data;

  useEffect(() => {
    if (!statusResult || !isTerminalStatus(statusResult.status)) {
      return;
    }

    if (reportedProcessIdRef.current === statusResult.processId) {
      return;
    }

    reportedProcessIdRef.current = statusResult.processId;
    setTrackedProcessId(null);
    queryClient.setQueryData(getCommunityImportCurrentQueryKey({ churchId, listId: listId || '' }), {});
    invalidateCommunityDataForChurch({ queryClient, churchId });
    notifyImportFinished({ result: statusResult });
  }, [churchId, listId, notifyImportFinished, queryClient, statusResult]);

  const statusError = statusQuery.error;

  useEffect(() => {
    if (!statusQuery.dataUpdatedAt || statusQuery.dataUpdatedAt === lastStatusDataUpdatedAtRef.current) {
      return;
    }

    lastStatusDataUpdatedAtRef.current = statusQuery.dataUpdatedAt;
    statusPollFailureCountRef.current = 0;
    setHasPollError(false);
  }, [statusQuery.dataUpdatedAt]);

  useEffect(() => {
    if (!statusError || isCommunityImportNotFoundError({ error: statusError })) {
      return;
    }

    statusPollFailureCountRef.current += 1;

    if (statusPollFailureCountRef.current >= COMMUNITY_IMPORT_MAX_STATUS_POLL_FAILURES) {
      setHasPollError(true);
    }
  }, [statusError]);

  // A missing process means the backend no longer tracks the run, so resync from the current endpoint.
  useEffect(() => {
    if (!statusError || !isCommunityImportNotFoundError({ error: statusError })) {
      return;
    }

    setTrackedProcessId(null);
    void refetchCurrentImport();
  }, [refetchCurrentImport, statusError]);

  const handleImportStarted = useCallback(({ processId }: HandleImportStartedParams) => {
    reportedProcessIdRef.current = null;
    statusPollFailureCountRef.current = 0;
    setHasPollError(false);
    setTrackedProcessId(processId);
  }, []);

  const retryImportStatus = useCallback(async () => {
    if (!trackedProcessId) {
      return;
    }

    statusPollFailureCountRef.current = 0;
    setHasPollError(false);
    await statusQuery.refetch();
  }, [statusQuery.refetch, trackedProcessId]);

  const isImporting = isEnabled && Boolean(trackedProcessId);

  return {
    isImporting,
    hasPollError,
    handleImportStarted,
    refetchCurrentImport,
    retryImportStatus,
  };
};
